import type { ReactNode } from "react";
import { Link } from "wouter";
import { Loader2, Lock } from "lucide-react";
import { InlineEdit } from "@/components/InlineEdit";
import { RecordPanel } from "@/components/record/RecordFrame";
import { dayLabel } from "@/components/record/recordFormat";
import { formatDealValue, parseDealValueToDollars } from "@/lib/formatDealValue";
import type { FeeDetail } from "@/hooks/useFinancials";
import type { OfferLine } from "@shared/offerMath";
import { cn } from "@/lib/utils";
import { MonthField } from "./MonthField";
import { SUBHEAD } from "./clientStyles";
import type { ClientData } from "./clientTypes";

type Save = (field: string, value: unknown) => void;
type SaveAsync = (field: string, value: unknown) => Promise<unknown>;
type FeeQuery = { data?: FeeDetail; isLoading: boolean; isError: boolean };

function Cell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className="m-0">{children}</dd>
    </div>
  );
}

/**
 * The deal in numbers. It shows the client's financials and every IOI and LOI
 * (the multiple is canonical and EV follows today's EBITDA, shared/offerMath.ts):
 * one line per buyer at their furthest bid; bids without terms are not listed.
 * For people with the financials grant it adds the fee terms, read from
 * /api/financials/fee-detail. Nothing here reads a fee field from the company row.
 */
export function DealSnapshotPanel({ company, saveField, saveFieldAsync, lines, linesLoading, today, fee, onOpenTracker }: {
  company: ClientData;
  saveField: Save;
  saveFieldAsync: SaveAsync;
  lines: OfferLine[];
  linesLoading: boolean;
  today: string;
  /** Present only for people with the financials grant. */
  fee?: FeeQuery;
  onOpenTracker: () => void;
}) {
  return (
    <RecordPanel title="Deal snapshot">
      <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] sm:grid-cols-4">
        <Cell label="Revenue">
          <InlineEdit label="Revenue" value={company.revenueRange} placeholder="e.g. $40M" addLabel="Add" onSave={(v) => saveFieldAsync("revenueRange", formatDealValue(v))} />
        </Cell>
        <Cell label="EBITDA">
          <InlineEdit label="EBITDA" value={company.ebitda ? formatDealValue(company.ebitda) : null} placeholder="e.g. $5.2M" addLabel="Add" onSave={(v) => saveFieldAsync("ebitda", v.trim() ? parseDealValueToDollars(v) : null)} />
        </Cell>
        <Cell label="Deal value">
          <InlineEdit label="Deal value" value={company.targetAmount ? formatDealValue(company.targetAmount) : null} placeholder="e.g. $40M" addLabel="Add" onSave={(v) => saveFieldAsync("targetAmount", parseDealValueToDollars(v))} />
        </Cell>
        <Cell label="Expected close">
          <MonthField label="Expected close" value={company.expectedCloseOn} onSave={(v) => saveField("expectedCloseOn", v)} />
        </Cell>
      </dl>

      <div className="mt-3 border-t pt-2.5">
        <div className="mb-1 flex items-center justify-between gap-2">
          <h4 className={cn(SUBHEAD, "flex items-center gap-1.5")}>
            IOIs and LOIs <span className="font-medium normal-case tracking-normal tabular-nums">{lines.length}</span>
          </h4>
          <button type="button" onClick={onOpenTracker} className="text-xs font-semibold text-primary-text hover:underline">Open Go-to-Market</button>
        </div>
        {linesLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : lines.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">No IOIs or LOIs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr className="border-b text-left text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-2 py-1 font-semibold">Buyer</th>
                  <th className="px-2 py-1 font-semibold">Stage</th>
                  <th className="px-2 py-1 font-semibold">Multiple</th>
                  <th className="px-2 py-1 font-semibold">EV</th>
                  <th className="px-2 py-1 font-semibold">Received</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.linkId} className={cn("border-b last:border-0", l.passed && "text-muted-foreground")}>
                    <td className="px-2 py-1.5 font-semibold">
                      <Link href={`/buyers/${l.buyerId}`} className="hover:underline">{l.buyerName}</Link>{l.passed && " (passed)"}
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={cn("rounded px-1.5 py-px text-[11px] font-semibold", l.stage === "LOI" && !l.passed ? "bg-primary/15 text-primary-text" : "bg-muted")}>{l.stage}</span>
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 tabular-nums">{l.multiple}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 tabular-nums" title={l.evDerived ? "From the multiple and today's EBITDA" : "As entered. Set EBITDA to derive it from the multiple."}>{l.ev}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 tabular-nums">{l.receivedOn ? dayLabel(l.receivedOn, today) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {fee && <FeeTerms fee={fee} saveField={saveField} saveFieldAsync={saveFieldAsync} />}
    </RecordPanel>
  );
}

/** Fee %, workfee and its start, and what lands at close. Granted users only; read from fee-detail. */
function FeeTerms({ fee, saveField, saveFieldAsync }: { fee: FeeQuery; saveField: Save; saveFieldAsync: SaveAsync }) {
  const f = fee.data;
  return (
    <div className="mt-3 border-t pt-2.5">
      <h4 className={cn(SUBHEAD, "mb-1.5 flex items-center gap-1")}>
        Fee terms <Lock className="h-2.5 w-2.5" aria-label="Only people with Financials access see this" />
      </h4>
      {fee.isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : fee.isError || !f ? (
        <p className="text-[13px] text-muted-foreground">Couldn't load fee terms.</p>
      ) : (
        <>
          <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] sm:grid-cols-3">
            <Cell label="Success fee %">
              <InlineEdit label="Expected fee percentage" value={f.feePct != null ? `${f.feePct}%` : null} placeholder="e.g. 3" addLabel="Add" onSave={(v) => { const n = parseFloat(String(v).replace(/[%\s]/g, "")); return saveFieldAsync("expectedFeePercentage", Number.isFinite(n) ? String(Math.max(0, Math.min(100, n))) : null); }} />
            </Cell>
            <Cell label="Monthly workfee">
              <InlineEdit label="Monthly workfee" value={f.workfeeMonthly ? formatDealValue(String(f.workfeeMonthly)) : null} placeholder="e.g. $10K" addLabel="Add" onSave={(v) => saveFieldAsync("workfeeMonthly", v.trim() ? parseDealValueToDollars(v) : null)} />
            </Cell>
            <Cell label="Workfee start">
              <MonthField label="Workfee start" value={f.workfeeStartOn} onSave={(v) => saveField("workfeeStartOn", v)} />
            </Cell>
          </dl>
          {f.successFee != null && (
            <div className="mt-2 rounded-md border bg-muted/30 px-3 py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-3 text-sm">
                <span>
                  <span className="text-xs text-muted-foreground">Success fee </span>
                  <span className="font-medium">{formatDealValue(String(f.successFee))}</span>
                </span>
                {f.credits > 0 && (
                  <span>
                    <span className="text-xs text-muted-foreground">Drawn to date </span>
                    <span className="font-medium">{formatDealValue(String(Math.round(f.workfeeAccrued + f.entryCredits)))}</span>
                  </span>
                )}
                {f.workfeeFuture > 0 && (
                  <span>
                    <span className="text-xs text-muted-foreground">Workfee to close </span>
                    <span className="font-medium">{formatDealValue(String(f.workfeeFuture))}</span>
                  </span>
                )}
                <span>
                  <span className="text-xs text-muted-foreground">Due at close </span>
                  <span className="font-semibold">{formatDealValue(String(f.balance ?? 0))}</span>
                </span>
              </div>
              {f.overdrawn && (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                  Workfees drawn exceed the success fee, so nothing further is due at close.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
